import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useNeura } from '../../contexts/NeuraContext.tsx';

const VisualizerContainer = styled(motion.div)`
  position: relative;
  height: 200px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 15px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 255, 136, 0.2);
  margin: 2rem 0;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
`;

const ThoughtMetrics = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-family: 'Courier New', monospace;
  color: #00ff88;
  font-size: 0.8rem;
  text-align: right;
`;

const SyncIndicator = styled(motion.div)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: #00ff88;
    border-radius: 50%;
    display: block;
  }
`;

export const QuantumThoughtVisualizer = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { neuralActivity, thoughtInput } = useNeura();
  const particlesRef = useRef<Array<{ x: number; y: number; vx: number; vy: number; size: number }>>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Initialize particles
    particlesRef.current = Array.from({ length: 100 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      size: Math.random() * 3 + 1
    }));

    let frame = 0;
    const animate = () => {
      frame++;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw neural connections
      ctx.strokeStyle = 'rgba(0, 255, 136, 0.2)';
      ctx.lineWidth = 1;

      particlesRef.current.forEach((particle, i) => {
        // Update particle position
        particle.x += particle.vx * (neuralActivity === 'processing' ? 2 : 1);
        particle.y += particle.vy * (neuralActivity === 'processing' ? 2 : 1);

        // Boundary check
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 255, 136, ${neuralActivity === 'processing' ? 0.8 : 0.5})`;
        ctx.fill();

        // Draw connections
        particlesRef.current.slice(i + 1).forEach(other => {
          const dx = other.x - particle.x;
          const dy = other.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        });
      });

      // Draw thought waves
      if (thoughtInput) {
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 255, 136, 0.5)';
        ctx.lineWidth = 2;
        
        for (let x = 0; x < canvas.width; x++) {
          const y = canvas.height / 2 + 
            Math.sin(x * 0.02 + frame * 0.05) * 30 * 
            Math.sin(frame * 0.02) * 
            (neuralActivity === 'processing' ? 1.5 : 1);
          
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      // Cleanup
    };
  }, [neuralActivity, thoughtInput]);

  return (
    <VisualizerContainer>
      <Canvas ref={canvasRef} />
      <SyncIndicator
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        Neural Sync Active
      </SyncIndicator>
      <ThoughtMetrics>
        SYNC RATE: {Math.floor(90 + Math.random() * 10)}%
        <br />
        QUANTUM COHERENCE: STABLE
        <br />
        THOUGHT PARTICLES: {particlesRef.current.length}
      </ThoughtMetrics>
    </VisualizerContainer>
  );
}; 